import type { UpdateProfileInput } from "@closer/shared";
import { updateProfileSchema } from "@closer/shared";
import { Button, Card, FormField, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * ⚠ YEREL TİP, `@closer/shared`'dan DEĞİL: profil görünümü sunucu tarafında
 * (`modules/profile/service.ts`) yaşıyor ve shared'a taşımak bu kartın kapsamı değil.
 * Aynı sayfadaki `PasswordSection` de aynı deseni kullanıyor; ihtiyaç duyulan alanlar
 * yazılıyor, fazlası değil.
 */
type ProfilGorunumu = {
  name: string;
  title: string | null;
  phone: string | null;
};

/**
 * KİŞİSEL PROFİL BİLGİLERİ (CLOS-1117 · bekçinin bulduğu yetim uç).
 *
 * ### ⚠ Neden bu bölüm var
 *
 * `PATCH /api/profile` sunucuda tam, testli ve çalışıyordu; hiçbir ekran onu çağırmıyordu.
 * Metot duyarlı erişilebilirlik bekçisi (`routeReachability.test.ts`) yakaladı: aynı yola
 * bir `GET` bağlı olduğu için uç "erişilebilir" sanılıyordu. Sonuç: kullanıcı adını,
 * unvanını ya da telefonunu üründen değiştiremiyordu.
 *
 * ### ⚠ DİL VE SAAT DİLİMİ BURADA DEĞİL
 *
 * Uç `locale`/`timezone` de kabul ediyor ama onların yeri Tercihler sayfası
 * (`PreferencesPage`, ADR-0023). Aynı alanı iki ekrandan yönetmek, iki ekranın gün gelip
 * ayrışması demek; bu bölüm yalnız KİMLİK alanlarını taşıyor.
 *
 * ### ⚠ Avatar adresi de burada değil
 *
 * Şema `image` alanını kabul ediyor ve protokolü girdide kesiyor, ama bir avatar YÜKLEME
 * akışı yok — kullanıcıya ham URL yazdırmak, olmayan bir yeteneği varmış gibi gösterirdi.
 * Alan sunucuda duruyor; yüzeyi kendi kartıyla gelir.
 */
export function ProfileSection() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const profil = useQuery({
    queryKey: ["profile"],
    /** ⚠ Uç zarfı `{ profile, fields }` döndürüyor; alan matrisi bu bölümün işi değil. */
    queryFn: async () => (await api<{ profile: ProfilGorunumu }>("/api/profile")).data.profile,
  });

  const form = useZodForm(updateProfileSchema, {
    defaultValues: { name: "", title: "", phone: "" } as UpdateProfileInput,
  });

  /**
   * ⚠ Sunucu verisi forma YÜKLENİYOR, `useState`'e KOPYALANMIYOR: alanlar boş açılsaydı
   * kaydetmek adı silerdi. `reset` yalnız veri geldiğinde çalışıyor.
   */
  const { reset } = form;
  useEffect(() => {
    if (!profil.data) return;
    reset({
      name: profil.data.name ?? "",
      title: profil.data.title ?? "",
      phone: profil.data.phone ?? "",
    } as UpdateProfileInput);
  }, [profil.data, reset]);

  const kaydet = useMutation({
    mutationFn: async (values: UpdateProfileInput) =>
      (
        await api<{ profile: ProfilGorunumu }>("/api/profile", {
          method: "PATCH",
          body: JSON.stringify({
            name: values.name,
            /** ⚠ Boş metin `null` olarak gidiyor: "silindi" ile "boş bırakıldı" aynı şey. */
            title: values.title?.trim() === "" ? null : values.title,
            phone: values.phone?.trim() === "" ? null : values.phone,
          }),
        })
      ).data,
    onSuccess: async (guncel) => {
      queryClient.setQueryData(["profile"], guncel.profile);
      /** Oturumdaki ad üst barda ve menüde görünüyor; bayat kalmasın. */
      await queryClient.invalidateQueries({ queryKey: ["session"] });
      toast.success("Profil bilgileriniz güncellendi");
    },
    onError: (hata: unknown) => {
      applyServerErrors(form, hata);
      toast.error(hata instanceof ApiRequestError ? hata.message : "Profil güncellenemedi");
    },
  });

  if (profil.isPending) {
    return (
      <Card title="Profil Bilgileri">
        <div role="status" aria-label="Profil yükleniyor">
          <Skeleton lines={3} />
        </div>
      </Card>
    );
  }

  if (profil.error) {
    return (
      <Card title="Profil Bilgileri">
        <StateView
          state={resolveErrorState(profil.error, { idempotent: true })}
          onRetry={() => void profil.refetch()}
        />
      </Card>
    );
  }

  return (
    <Card title="Profil Bilgileri">
      <form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((values) => kaydet.mutate(values))}
      >
        <p className="text-sm text-fg-muted">
          Bu bilgiler çalışma alanlarınızda sizi tanıtır; giriş adresiniz ayrıca yönetilir.
        </p>

        <FormField label="Ad Soyad" error={form.formState.errors.name?.message}>
          <Input id="profil-ad" autoComplete="name" {...form.register("name")} />
        </FormField>

        <FormField label="Unvan" error={form.formState.errors.title?.message}>
          <Input id="profil-unvan" autoComplete="organization-title" {...form.register("title")} />
        </FormField>

        <FormField label="Telefon" error={form.formState.errors.phone?.message}>
          <Input id="profil-telefon" autoComplete="tel" {...form.register("phone")} />
        </FormField>

        <div className="flex justify-end">
          <Button type="submit" disabled={kaydet.isPending}>
            {kaydet.isPending ? "Kaydediliyor…" : "Değişiklikleri kaydet"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
